import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import { notification, passwordRegex } from "../../../utils/utility";
import { connect } from "react-redux";
import { updateMyChangePassword } from "../../../actions/myChangePassword";
import Loader from "../../../components/Loader/Loader";
import axios from "../../../axios-instance";

/* ─── Design tokens ─────────────────────────────────────────────────────── */
const C = {
  navy:        "#0c4767",
  navyLight:   "#e8f1f7",
  orange:      "#FE9920",
  orangeLight: "#FFF7ED",
  dark:        "#111827",
  mid:         "#374151",
  muted:       "#6B7280",
  faint:       "#9CA3AF",
  border:      "#E5E7EB",
  surface:     "#F9FAFB",
  white:       "#ffffff",
  green:       "#10B981",
  greenBg:     "#F0FDF4",
  greenBorder: "#BBF7D0",
  greenDark:   "#065F46",
  red:         "#EF4444",
  redLight:    "#FEF2F2",
};

const STYLES = `
  .cp-field {
    width: 100%;
    border: 2px solid #E5E7EB;
    border-radius: 10px;
    padding: 11px 44px 11px 14px;
    font-size: 14px;
    color: #111827;
    outline: none;
    background: #fff;
    transition: border-color 0.15s, box-shadow 0.15s;
    box-sizing: border-box;
    font-family: inherit;
  }
  .cp-field:focus {
    border-color: #0c4767 !important;
    box-shadow: 0 0 0 3px rgba(12,71,103,0.15) !important;
  }
  .cp-field.is-error { border-color: #EF4444 !important; }
  .cp-field.is-valid { border-color: #10B981 !important; }
  .cp-submit:not(:disabled):hover { background: #0a3d55 !important; }
  .cp-eye-btn { cursor: pointer; background: none; border: none; padding: 4px; display: flex; align-items: center; }
  .cp-eye-btn:focus-visible { outline: 2px solid #0c4767; border-radius: 4px; }
`;

/* ─── Password strength ─────────────────────────────────────────────────── */
const getStrength = (pwd) => {
  if (!pwd) return 0;
  let score = 0;
  if (pwd.length >= 8)  score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  return score;
};

const strengthLabel = ["", "Weak", "Fair", "Good", "Strong"];
const strengthColor = ["", C.red, "#F59E0B", "#3B82F6", C.green];

/* ─── Eye icon ──────────────────────────────────────────────────────────── */
const EyeIcon = ({ visible }) => visible ? (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
) : (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);

/* ─── Password field with toggle ────────────────────────────────────────── */
const PasswordInput = ({ id, name, value, onChange, onBlur, placeholder, hasError, hasSuccess }) => {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <input
        id={id}
        name={name}
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        autoComplete={name === "password" ? "current-password" : "new-password"}
        className={`cp-field${hasError ? " is-error" : hasSuccess ? " is-valid" : ""}`}
        aria-invalid={!!hasError}
        aria-describedby={hasError ? `${id}-err` : undefined}
      />
      <button
        type="button"
        className="cp-eye-btn"
        onClick={() => setShow(!show)}
        aria-label={show ? "Hide password" : "Show password"}
        tabIndex={-1}
        style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)" }}
      >
        <EyeIcon visible={show} />
      </button>
    </div>
  );
};

/* ─── Field error message ───────────────────────────────────────────────── */
const FieldError = ({ id, message }) => (
  <p id={id} role="alert" style={{ margin: "5px 0 0", fontSize: "12px", color: C.red }}>
    {message}
  </p>
);

/* ─── Yup schema ────────────────────────────────────────────────────────── */
const buildSchema = (hasPassword) =>
  Yup.object().shape({
    ...(hasPassword
      ? { password: Yup.string().required("Current password is required.") }
      : {}),
    new_password: Yup.string()
      .matches(passwordRegex, "Must contain uppercase, number, and special character.")
      .required("New password is required."),
    confirm_password: Yup.string()
      .oneOf([Yup.ref("new_password"), null], "Passwords do not match.")
      .required("Please confirm your new password."),
  });

/* ─── Main component ────────────────────────────────────────────────────── */
const ProfileChangePassword = (props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasPassword, setHasPassword] = useState(true);
  const [loaded, setLoaded]           = useState(false);

  useEffect(() => {
    axios.get("user-details")
      .then((res) => {
        const data = res.data?.data || res.data || {};
        setHasPassword(data.has_password !== false);
        setLoaded(true);
      })
      .catch(() => { setHasPassword(true); setLoaded(true); });
  }, []);

  useEffect(() => {
    if (props.IsDataSubmitedSuccessfullyChangePassword) {
      setIsLoading(false);
      notification.success(hasPassword ? "Password updated successfully." : "Password set successfully.");
      setHasPassword(true);
    }
    if (props.IsError) {
      setIsLoading(false);
      notification.error(props.errorMessage || "Unable to change password.");
    }
  }, [props.IsDataSubmitedSuccessfullyChangePassword, props.IsError, props.errorMessage, hasPassword]);

  if (!loaded) return <Loader isShowLoader={true} />;

  return (
    <>
      <style>{STYLES}</style>
      <Loader isShowLoader={isLoading} />

      <div style={{ padding: "32px 28px", maxWidth: "960px" }}>

        {/* ── Section header ──────────────────────────────────────── */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: "14px", marginBottom: "28px" }}>
          <div style={{
            width: 48, height: 48, borderRadius: "12px", background: C.navyLight,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke={C.navy} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: C.dark }}>
              {hasPassword ? "Change Password" : "Set Password"}
            </h2>
            <p style={{ margin: "6px 0 0", fontSize: "13px", color: C.muted, lineHeight: 1.6 }}>
              {hasPassword
                ? "Update your password regularly to keep your account secure."
                : "You signed in with Google or Microsoft. Set a password to also log in with your email."}
            </p>
          </div>
        </div>

        <Formik
          initialValues={{ password: "", new_password: "", confirm_password: "" }}
          validationSchema={buildSchema(hasPassword)}
          enableReinitialize
          onSubmit={(values, { resetForm }) => {
            setIsLoading(true);
            const payload = {
              new_password:     values.new_password,
              confirm_password: values.confirm_password,
            };
            if (hasPassword) payload.password = values.password;
            props.updateMyChangePassword(payload);
            resetForm();
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur }) => {
            const strength = getStrength(values.new_password);
            const matchOk  = values.confirm_password && values.confirm_password === values.new_password;

            return (
              <Form noValidate>
                <div style={{
                  background: C.white, border: `1px solid ${C.border}`,
                  borderRadius: "16px", padding: "24px",
                  display: "flex", flexDirection: "column", gap: "20px",
                }}>

                  {/* ── Top row: current password full-width (only when hasPassword) ── */}
                  {hasPassword && (
                    <>
                      <div>
                        <label htmlFor="cp-current" style={{ display: "block", fontSize: "13px", fontWeight: 600, color: C.mid, marginBottom: "7px" }}>
                          Current password <span style={{ color: C.red }}>*</span>
                        </label>
                        <PasswordInput
                          id="cp-current"
                          name="password"
                          value={values.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="Enter your current password"
                          hasError={errors.password && touched.password}
                        />
                        {errors.password && touched.password && (
                          <FieldError id="cp-current-err" message={errors.password} />
                        )}
                      </div>
                      <div style={{ height: 1, background: C.border, margin: "0 -24px" }} />
                    </>
                  )}

                  {/* ── Bottom row: new + confirm side by side ─────────────────── */}
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "20px",
                    alignItems: "start",
                  }}>
                    {/* New password */}
                    <div>
                      <label htmlFor="cp-new" style={{ display: "block", fontSize: "13px", fontWeight: 600, color: C.mid, marginBottom: "7px" }}>
                        New password <span style={{ color: C.red }}>*</span>
                      </label>
                      <PasswordInput
                        id="cp-new"
                        name="new_password"
                        value={values.new_password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Create a strong password"
                        hasError={errors.new_password && touched.new_password}
                        hasSuccess={!errors.new_password && values.new_password}
                      />
                      {errors.new_password && touched.new_password && (
                        <FieldError id="cp-new-err" message={errors.new_password} />
                      )}
                      {/* Strength bar */}
                      {values.new_password && (
                        <div style={{ marginTop: "10px" }}>
                          <div style={{ display: "flex", gap: "4px", marginBottom: "5px" }}>
                            {[1, 2, 3, 4].map((lvl) => (
                              <div key={lvl} style={{
                                flex: 1, height: 4, borderRadius: 999,
                                background: lvl <= strength ? strengthColor[strength] : C.border,
                                transition: "background 0.2s",
                              }} />
                            ))}
                          </div>
                          <p style={{ margin: 0, fontSize: "11.5px", fontWeight: 600, color: strengthColor[strength] }}>
                            {strengthLabel[strength]}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Confirm password */}
                    <div>
                      <label htmlFor="cp-confirm" style={{ display: "block", fontSize: "13px", fontWeight: 600, color: C.mid, marginBottom: "7px" }}>
                        Confirm new password <span style={{ color: C.red }}>*</span>
                      </label>
                      <PasswordInput
                        id="cp-confirm"
                        name="confirm_password"
                        value={values.confirm_password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Re-enter your new password"
                        hasError={errors.confirm_password && touched.confirm_password}
                        hasSuccess={matchOk}
                      />
                      {errors.confirm_password && touched.confirm_password && (
                        <FieldError id="cp-confirm-err" message={errors.confirm_password} />
                      )}
                      {matchOk && !errors.confirm_password && (
                        <p style={{ margin: "5px 0 0", fontSize: "12px", color: C.green, fontWeight: 600 }}>
                          ✓ Passwords match
                        </p>
                      )}
                    </div>
                  </div>

                  {/* ── Requirements hint (full width) ────────────────────────── */}
                  <div style={{
                    background: C.surface, borderRadius: "10px",
                    padding: "12px 14px", display: "flex", gap: "10px", alignItems: "flex-start",
                  }}>
                    <svg width="14" height="14" viewBox="0 0 20 20" fill={C.faint} style={{ flexShrink: 0, marginTop: "1px" }}>
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
                    </svg>
                    <p style={{ margin: 0, fontSize: "12px", color: C.muted, lineHeight: 1.7 }}>
                      Password must include at least <strong>8 characters</strong>,
                      one <strong>uppercase letter</strong>, one <strong>number</strong>,
                      and one <strong>special character</strong> (e.g.&nbsp;!@#$%).
                    </p>
                  </div>

                  {/* ── Submit ────────────────────────────────────────────────── */}
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <button
                      type="submit"
                      className="cp-submit"
                      disabled={isLoading}
                      style={{
                        background: C.navy, color: C.white, border: "none",
                        borderRadius: "10px", padding: "12px 32px",
                        fontSize: "14px", fontWeight: 700, cursor: isLoading ? "not-allowed" : "pointer",
                        display: "flex", alignItems: "center", gap: "8px",
                        transition: "background 0.15s",
                        boxShadow: "0 2px 8px rgba(12,71,103,0.28)",
                        opacity: isLoading ? 0.7 : 1,
                      }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                      {hasPassword ? "Update password" : "Set password"}
                    </button>
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  MyChangePasswordSuccess: state.myChangePassword.MyChangePasswordSuccess,
  IsDataSubmitedSuccessfullyChangePassword: state.myChangePassword.IsDataSubmitedSuccessfullyChangePassword,
  IsError: state.myChangePassword.IsError,
  errorMessage: state.myChangePassword.errorMessage,
});

export default connect(mapStateToProps, { updateMyChangePassword })(ProfileChangePassword);
